import { commissionAmountCents } from './commission';
import { revenueCents } from './engine';
import type { ExportLabels } from './exportCsv';
import { formatReportValue } from './format';
import { METRIC_BY_ID } from './metrics';
import { formatCompactPeriodLabel } from './periods';
import {
  CARD_TINTS,
  COLORS,
  CONTENT_WIDTH,
  MARGIN,
  PdfDocument,
  drawReportHeader,
  measureText,
  pdfSafeText,
  wrapText,
} from './pdfKit';
import {
  FINANCIAL_METRICS,
  planPractitionerPdf,
  type PdfFigure,
  type PdfMixItem,
  type PdfSection,
  type PdfTable,
  type PractitionerPdfPlan,
} from './pdfReportPlan';
import { inferSourceType } from './revenueEvents';
import type { ReportResult, ReportRow } from './types';

export interface PdfExportOptions {
  includeDetailedRecords?: boolean;
  generatedAt?: Date;
  locale?: string;
}

const SECTION_TITLES: Record<PdfSection['kind'], string> = {
  financial: 'Financial overview',
  commission: 'Commission',
  appointments: 'Appointment performance',
  patients: 'Patient performance',
  retention: 'Patient retention',
  services: 'Service performance',
  comparison: 'Practitioner overview',
  mix: 'Service mix',
  other: 'Other metrics',
};

function prettyMetricName(metricId: string, raw: string): string {
  const collapsed = raw.replaceAll('_', ' ').trim();
  if (collapsed.toLowerCase() === metricId.replaceAll('_', ' ') || collapsed === collapsed.toLowerCase()) {
    return collapsed.charAt(0).toUpperCase() + collapsed.slice(1);
  }
  return raw;
}

function metricLabel(metricId: string, labels: ExportLabels): string {
  const metric = METRIC_BY_ID[metricId];
  const raw = labels.metricLabels[metric?.labelKey ?? ''] ?? labels.metricLabels[metricId] ?? metricId;
  return prettyMetricName(metricId, raw);
}

function formatMetric(
  metricId: string,
  value: import('./types').ReportValue,
  currency: string,
  locale: string,
): string {
  const metric = METRIC_BY_ID[metricId];
  return pdfSafeText(formatReportValue(value, metric?.format ?? 'text', currency, locale));
}

function numericAlign(metricId: string): 'left' | 'right' {
  const format = METRIC_BY_ID[metricId]?.format;
  return format === 'text' ? 'left' : 'right';
}

function billingLabel(method: string | null, labels: ExportLabels): string {
  if (!method) return '—';
  if (method === 'punch_card') return copy(labels, 'punchCard', 'Punch card');
  if (method === 'invoice') return copy(labels, 'invoice', 'Invoice');
  return method;
}

function copy(labels: ExportLabels, key: keyof NonNullable<ExportLabels['pdf']>, fallback: string): string {
  return labels.pdf?.[key] ?? fallback;
}

function sectionTitle(kind: PdfSection['kind'], labels: ExportLabels): string {
  const keys: Partial<Record<PdfSection['kind'], keyof NonNullable<ExportLabels['pdf']>>> = {
    financial: 'financialOverview',
    commission: 'commission',
    appointments: 'appointmentPerformance',
    patients: 'patientPerformance',
    retention: 'patientRetention',
    services: 'servicePerformance',
    comparison: 'practitionerOverview',
    mix: 'serviceMix',
    other: 'otherMetrics',
  };
  const key = keys[kind];
  return key ? copy(labels, key, SECTION_TITLES[kind]) : SECTION_TITLES[kind];
}

function tableHeading(table: PdfTable, labels: ExportLabels): string {
  const ids = table.columns.map((column) => column.metricId);
  if (ids.some((id) => id === 'commission_amount' || id === 'commission_percentage')) {
    return copy(labels, 'commissionOverview', 'Commission by service');
  }
  if (ids.some((id) => (FINANCIAL_METRICS as readonly string[]).includes(id))) {
    return copy(labels, 'revenueByService', 'Revenue by service');
  }
  return copy(labels, 'servicePerformance', SECTION_TITLES.services);
}

function drawHeader(doc: PdfDocument, result: ReportResult, labels: ExportLabels, generatedAt: Date, locale: string) {
  const practitionerNames = [
    ...new Set(
      result.facts
        .map((fact) => fact.practitioner_name?.trim())
        .filter((name): name is string => Boolean(name)),
    ),
  ];
  const clinic = result.facts.find((fact) => fact.clinic_name)?.clinic_name;
  const meta = [
    ...(clinic ? [{ label: copy(labels, 'clinicLabel', 'Clinic'), value: clinic }] : []),
    {
      label: copy(labels, 'practitionerLabel', 'Practitioner'),
      value:
        practitionerNames.length === 0
          ? copy(labels, 'allPractitioners', 'All practitioners')
          : practitionerNames.length <= 3
            ? practitionerNames.join(', ')
            : copy(labels, 'allPractitioners', 'All practitioners'),
    },
    { label: copy(labels, 'periodLabel', 'Period'), value: formatCompactPeriodLabel(result.period, locale) },
    {
      label: copy(labels, 'generatedLabel', 'Generated'),
      value: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(generatedAt),
    },
  ];
  drawReportHeader(doc, {
    brand: copy(labels, 'brand', 'Clinio'),
    title: copy(labels, 'performanceTitle', labels.title),
    meta,
  });
}

function drawSectionTitle(doc: PdfDocument, title: string) {
  doc.ensureSpace(28);
  doc.text(title.toUpperCase(), MARGIN, doc.y - 1, {
    size: 8,
    bold: true,
    color: COLORS.lavenderAccent,
  });
  doc.gap(5);
  doc.line(MARGIN, doc.y, MARGIN + 22, doc.y, COLORS.accent, 1.1);
  doc.gap(11);
}

function fitValueSize(value: string, maxWidth: number, preferred: number): number {
  let size = preferred;
  while (size > 8 && measureText(value, size, true) > maxWidth) size -= 0.5;
  return size;
}

function drawKeyFigureCards(
  doc: PdfDocument,
  figures: PdfFigure[],
  result: ReportResult,
  labels: ExportLabels,
  locale: string,
) {
  if (figures.length === 0) return;
  const count = Math.min(figures.length, 5);
  const items = figures.slice(0, count);
  const gap = 7;
  const cols = items.length;
  const cardW = items.length === 1 ? (CONTENT_WIDTH - gap * 2) / 3 : (CONTENT_WIDTH - gap * (cols - 1)) / cols;
  const prepared = items.map((figure, index) => {
    const label = metricLabel(figure.metricId, labels);
    const value = formatMetric(figure.metricId, figure.value, result.currency, locale);
    const labelLines = wrapText(label, cardW - 16, 7);
    return { labelLines, value, tint: CARD_TINTS[index % CARD_TINTS.length] };
  });
  const cardH = Math.max(48, ...prepared.map((item) => 14 + item.labelLines.length * 9 + 18));
  doc.ensureSpace(cardH + 6);
  prepared.forEach((item, index) => {
    const x = MARGIN + index * (cardW + gap);
    const bottom = doc.y - cardH;
    doc.fillRoundedRect(x, bottom, cardW, cardH, 7, item.tint.bg);
    doc.strokeRoundedRect(x, bottom, cardW, cardH, 7, COLORS.line, 0.45);
    doc.fillRoundedRect(x + 8, bottom + 10, 2.2, cardH - 20, 1, item.tint.accent);
    item.labelLines.forEach((line, lineIndex) => {
      doc.text(line, x + 14, doc.y - 13 - lineIndex * 9, {
        size: 7,
        color: COLORS.muted,
        maxWidth: cardW - 20,
      });
    });
    const valueSize = fitValueSize(item.value, cardW - 20, 12);
    doc.text(item.value, x + 14, doc.y - 16 - item.labelLines.length * 9 - 8, {
      size: valueSize,
      bold: true,
      maxWidth: cardW - 20,
    });
  });
  doc.y -= cardH;
  doc.gap(14);
}

function drawMetricPanel(
  doc: PdfDocument,
  figures: PdfFigure[],
  result: ReportResult,
  labels: ExportLabels,
  locale: string,
) {
  if (figures.length === 0) return;
  const cols = figures.length === 1 ? 1 : 2;
  const colW = (CONTENT_WIDTH - 20) / cols;
  const rowH = 28;
  const rows = Math.ceil(figures.length / cols);
  const pad = 10;
  const height = rows * rowH + pad * 2;
  doc.ensureSpace(height + 8);
  const bottom = doc.y - height;
  doc.fillRoundedRect(MARGIN, bottom, CONTENT_WIDTH, height, 7, COLORS.paper);
  doc.strokeRoundedRect(MARGIN, bottom, CONTENT_WIDTH, height, 7, COLORS.line, 0.55);
  figures.forEach((figure, index) => {
    const col = index % cols;
    const row = Math.floor(index / cols);
    const x = MARGIN + 12 + col * (colW + 8);
    const top = doc.y - pad - 8 - row * rowH;
    doc.text(metricLabel(figure.metricId, labels), x, top, {
      size: 7,
      color: COLORS.muted,
      maxWidth: colW - 8,
    });
    doc.text(formatMetric(figure.metricId, figure.value, result.currency, locale), x, top - 12, {
      size: 10,
      bold: true,
      maxWidth: colW - 8,
    });
  });
  doc.y -= height;
  doc.gap(12);
}

function drawMix(doc: PdfDocument, items: PdfMixItem[], locale: string) {
  if (items.length === 0) return;
  const barH = 4;
  const rowH = 22;
  const height = items.length * rowH + 4;
  doc.ensureSpace(height + 6);
  for (const item of items) {
    const percent = pdfSafeText(formatReportValue(item.share, 'percent', 'DKK', locale));
    const labelWidth = CONTENT_WIDTH - 52;
    doc.text(item.label, MARGIN, doc.y - 8, { size: 8, maxWidth: labelWidth });
    doc.text(percent, MARGIN, doc.y - 8, { size: 8, bold: true, align: 'right', maxWidth: CONTENT_WIDTH });
    doc.gap(11);
    const fill = Math.max(2, CONTENT_WIDTH * Math.min(1, Math.max(0, item.share)));
    doc.fillRoundedRect(MARGIN, doc.y - barH, CONTENT_WIDTH, barH, 2, COLORS.barTrack);
    if (fill > 0) doc.fillRoundedRect(MARGIN, doc.y - barH, fill, barH, 2, COLORS.bar);
    doc.gap(11);
  }
  doc.gap(6);
}

function drawNote(doc: PdfDocument, text: string) {
  const lines = wrapText(text, CONTENT_WIDTH, 8);
  doc.ensureSpace(lines.length * 10 + 8);
  for (const line of lines) {
    doc.text(line, MARGIN, doc.y - 1, { size: 8, color: COLORS.muted });
    doc.gap(10);
  }
  doc.gap(4);
}

function allocateColumnWidths(table: PdfTable, labels: ExportLabels, result: ReportResult, locale: string): number[] {
  const identityMin = 120;
  const numericMins = table.columns.map((column) => {
    const header = measureText(metricLabel(column.metricId, labels), 7, true);
    const samples = [...table.rows, table.totals]
      .filter(Boolean)
      .map((row) => measureText(formatMetric(column.metricId, row!.values[column.metricId], result.currency, locale), 8));
    return Math.min(96, Math.max(header, ...samples, 44) + 12);
  });
  const numericTotal = numericMins.reduce((sum, width) => sum + width, 0);
  const identityWidth = Math.max(identityMin, CONTENT_WIDTH - numericTotal);
  if (identityWidth + numericTotal <= CONTENT_WIDTH) return [identityWidth, ...numericMins];
  const scale = (CONTENT_WIDTH - identityMin) / Math.max(numericTotal, 1);
  return [identityMin, ...numericMins.map((width) => Math.max(48, width * scale))];
}

function drawTable(
  doc: PdfDocument,
  table: PdfTable,
  result: ReportResult,
  labels: ExportLabels,
  locale: string,
  identityTitle: string,
) {
  if (table.columns.length === 0 || table.rows.length === 0) return;
  const widths = allocateColumnWidths(table, labels, result, locale);
  const headers = [identityTitle, ...table.columns.map((column) => metricLabel(column.metricId, labels))];
  const rowHeightFor = (row: ReportRow, bold = false) => {
    const nameLines = wrapText(row.label, widths[0] - 12, 8, bold);
    return Math.max(18, nameLines.length * 10 + 8);
  };

  const headerLines = headers.map((header, index) => wrapText(header, widths[index] - 8, 7, true));
  const headerH = Math.max(18, Math.max(...headerLines.map((lines) => lines.length)) * 9 + 8);
  const drawHeaderRow = () => {
    doc.ensureSpace(headerH + 20);
    doc.fillRect(MARGIN, doc.y - headerH, CONTENT_WIDTH, headerH, COLORS.headerBg);
    let x = MARGIN;
    headerLines.forEach((lines, index) => {
      const align = index === 0 ? 'left' : 'right';
      lines.forEach((line, lineIndex) => {
        doc.text(line, x + 5, doc.y - 12 - lineIndex * 9, {
          size: 7,
          bold: true,
          color: COLORS.muted,
          align,
          maxWidth: widths[index] - 10,
        });
      });
      x += widths[index];
    });
    doc.y -= headerH;
  };

  drawHeaderRow();
  const body = table.rows.filter((row) => row.grouping !== 'subtotal' && row.grouping !== 'total');
  body.forEach((row, rowIndex) => {
    const height = rowHeightFor(row);
    if (doc.y - height < 56) {
      doc.newPage();
      drawHeaderRow();
    }
    if (rowIndex % 2 === 1) {
      doc.fillRect(MARGIN, doc.y - height, CONTENT_WIDTH, height, COLORS.altRow);
    }
    let x = MARGIN;
    const nameLines = wrapText(row.label, widths[0] - 12, 8);
    nameLines.forEach((line, lineIndex) => {
      doc.text(line, x + 5, doc.y - 12 - lineIndex * 10, { size: 8, maxWidth: widths[0] - 10 });
    });
    x += widths[0];
    table.columns.forEach((column, index) => {
      doc.text(
        formatMetric(column.metricId, row.values[column.metricId], result.currency, locale),
        x + 4,
        doc.y - 12,
        { size: 8, align: numericAlign(column.metricId), maxWidth: widths[index + 1] - 8 },
      );
      x += widths[index + 1];
    });
    doc.y -= height;
  });
  if (table.totals) {
    const height = rowHeightFor(table.totals, true);
    doc.ensureSpace(height + 4);
    doc.fillRect(MARGIN, doc.y - height, CONTENT_WIDTH, height, COLORS.totalBg);
    let x = MARGIN;
    const totalLabel =
      table.identityKind === 'service' ? copy(labels, 'clinicTotal', 'Total') : table.totals.label;
    doc.text(totalLabel, x + 5, doc.y - 12, { size: 8, bold: true, maxWidth: widths[0] - 10 });
    x += widths[0];
    table.columns.forEach((column, index) => {
      doc.text(
        formatMetric(column.metricId, table.totals!.values[column.metricId], result.currency, locale),
        x + 4,
        doc.y - 12,
        { size: 8, bold: true, align: numericAlign(column.metricId), maxWidth: widths[index + 1] - 8 },
      );
      x += widths[index + 1];
    });
    doc.y -= height;
  }
  doc.gap(12);
}

function detailColumns(
  result: ReportResult,
  labels: ExportLabels,
  commissionConfigured: boolean,
): { key: string; title: string; width: number; align: 'left' | 'right' }[] {
  const practitioners = new Set(
    result.facts.map((fact) => fact.practitioner_id).filter((id): id is string => Boolean(id)),
  );
  const showCommission =
    commissionConfigured &&
    (result.metricIds.includes('commission_amount') || result.metricIds.includes('commission_percentage'));
  const columns = [
    { key: 'date', title: labels.detailHeaders.date, width: 70, align: 'left' as const },
    { key: 'patient', title: labels.detailHeaders.patient, width: 92, align: 'left' as const },
    ...(practitioners.size > 1
      ? [{ key: 'practitioner', title: labels.detailHeaders.practitioner, width: 88, align: 'left' as const }]
      : []),
    { key: 'service', title: labels.detailHeaders.service, width: 110, align: 'left' as const },
    { key: 'billing', title: labels.detailHeaders.billingMethod, width: 88, align: 'left' as const },
    { key: 'revenue', title: labels.detailHeaders.recognizedRevenue, width: 72, align: 'right' as const },
    ...(showCommission
      ? [{ key: 'commission', title: labels.detailHeaders.commissionAmount, width: 68, align: 'right' as const }]
      : []),
  ];
  const total = columns.reduce((sum, column) => sum + column.width, 0);
  if (Math.abs(total - CONTENT_WIDTH) < 1) return columns;
  const scale = CONTENT_WIDTH / total;
  return columns.map((column) => ({ ...column, width: column.width * scale }));
}

function drawDetails(
  doc: PdfDocument,
  result: ReportResult,
  labels: ExportLabels,
  locale: string,
  commissionConfigured: boolean,
) {
  const facts = result.facts.filter((fact) => fact.fact_kind === 'appointment');
  if (facts.length === 0) return;
  doc.ensureSpace(72);
  drawSectionTitle(doc, copy(labels, 'detailedRecords', 'Detailed treatment records'));
  const columns = detailColumns(result, labels, commissionConfigured);

  const cell = (fact: (typeof facts)[number], key: string): string => {
    const recognized = revenueCents(fact, result.revenueBasis);
    const currency = fact.currency || result.currency;
    if (key === 'date') {
      return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(
        new Date(fact.starts_at),
      );
    }
    if (key === 'patient') return fact.patient_name ?? '—';
    if (key === 'practitioner') return fact.practitioner_name ?? '—';
    if (key === 'service') return fact.service_name ?? '—';
    if (key === 'billing') {
      const method = billingLabel(fact.billing_method, labels);
      if (inferSourceType(fact) === 'punch_card_redemption' && fact.clips_redeemed) {
        const clip = copy(labels, 'clip', 'klip');
        return `${method} (${fact.clips_redeemed} ${clip})`;
      }
      return method;
    }
    if (key === 'revenue') return pdfSafeText(formatReportValue(recognized, 'currency', currency, locale));
    return pdfSafeText(
      formatReportValue(commissionAmountCents(recognized, fact.commission_percent), 'currency', currency, locale),
    );
  };

  const drawHeaderRow = () => {
    const headerH = 17;
    doc.ensureSpace(headerH + 18);
    doc.fillRect(MARGIN, doc.y - headerH, CONTENT_WIDTH, headerH, COLORS.headerBg);
    let x = MARGIN;
    for (const column of columns) {
      doc.text(column.title, x + 3, doc.y - 11, {
        size: 7,
        bold: true,
        color: COLORS.muted,
        align: column.align,
        maxWidth: column.width - 6,
      });
      x += column.width;
    }
    doc.y -= headerH;
  };

  drawHeaderRow();
  facts.forEach((fact, rowIndex) => {
    const linesPerCol = columns.map((column) => wrapText(cell(fact, column.key), column.width - 6, 7));
    const lineCount = Math.max(...linesPerCol.map((lines) => lines.length), 1);
    const height = lineCount * 9 + 6;
    if (doc.y - height < 56) {
      doc.newPage();
      drawHeaderRow();
    }
    if (rowIndex % 2 === 1) {
      doc.fillRect(MARGIN, doc.y - height, CONTENT_WIDTH, height, COLORS.altRow);
    }
    let x = MARGIN;
    columns.forEach((column, index) => {
      linesPerCol[index].forEach((line, lineIndex) => {
        doc.text(line, x + 3, doc.y - 10 - lineIndex * 9, {
          size: 7,
          align: column.align,
          maxWidth: column.width - 6,
        });
      });
      x += column.width;
    });
    doc.y -= height;
  });
}

function sectionHasContent(section: PdfSection): boolean {
  return (
    section.figures.length > 0 ||
    section.tables.some((table) => table.rows.length > 0) ||
    Boolean(section.mix && section.mix.length > 0) ||
    Boolean(section.note)
  );
}

function drawSection(
  doc: PdfDocument,
  section: PdfSection,
  result: ReportResult,
  labels: ExportLabels,
  locale: string,
  figureStyle: 'cards' | 'panel',
) {
  if (!sectionHasContent(section)) return;
  const primaryTitle =
    section.kind === 'services' && section.tables[0]
      ? tableHeading(section.tables[0], labels)
      : sectionTitle(section.kind, labels);
  drawSectionTitle(doc, primaryTitle);
  if (section.note === 'commission_unconfigured') {
    drawNote(
      doc,
      copy(
        labels,
        'commissionUnconfigured',
        'Commission rates have not been configured for this report.',
      ),
    );
  }
  if (figureStyle === 'cards') drawKeyFigureCards(doc, section.figures, result, labels, locale);
  else drawMetricPanel(doc, section.figures, result, labels, locale);
  if (section.note === 'retention') {
    drawNote(
      doc,
      labels.pdf?.retentionNote ??
        `Retention: share of patients seen in the period who returned within ${result.retentionFollowUpDays} days.`,
    );
  }
  if (section.mix) drawMix(doc, section.mix, locale);
  for (const table of section.tables) {
    if (table.rows.length === 0) continue;
    if (section.kind === 'commission') {
      const heading = tableHeading(table, labels);
      if (heading.toUpperCase() !== primaryTitle.toUpperCase()) {
        drawSectionTitle(doc, heading);
      }
    }
    drawTable(
      doc,
      table,
      result,
      labels,
      locale,
      table.identityKind === 'practitioner' ? labels.practitioner : labels.service,
    );
  }
}

function renderPlan(
  doc: PdfDocument,
  plan: PractitionerPdfPlan,
  result: ReportResult,
  labels: ExportLabels,
  locale: string,
) {
  if (plan.keyFigures.length > 0) {
    drawSectionTitle(doc, copy(labels, 'keyFigures', 'Key figures'));
    drawKeyFigureCards(doc, plan.keyFigures, result, labels, locale);
  }

  const comparison = plan.sections.filter((section) => section.kind === 'comparison');
  const commission = plan.sections.filter((section) => section.kind === 'commission');
  const rest = plan.sections.filter((section) => section.kind !== 'comparison' && section.kind !== 'commission');

  for (const section of comparison) {
    drawSection(doc, section, result, labels, locale, 'panel');
  }
  for (const section of commission) {
    drawSection(doc, section, result, labels, locale, 'cards');
  }

  const multiBlocks = plan.practitionerBlocks.length > 1;
  for (const block of plan.practitionerBlocks) {
    if (multiBlocks) {
      doc.ensureSpace(28);
      doc.text(block.row.label, MARGIN, doc.y - 1, { size: 10, bold: true });
      doc.gap(12);
    }
    for (const table of block.tables) {
      if (table.rows.length === 0) continue;
      drawSectionTitle(doc, tableHeading(table, labels));
      drawTable(doc, table, result, labels, locale, labels.service);
    }
  }

  for (const section of rest) {
    drawSection(doc, section, result, labels, locale, 'panel');
  }

  if (plan.includeDetails) {
    drawDetails(doc, result, labels, locale, plan.commissionConfigured);
  }
}

export function reportToPdf(
  result: ReportResult,
  labels: ExportLabels,
  options: PdfExportOptions = {},
): Uint8Array {
  const locale = options.locale ?? 'da-DK';
  const generatedAt = options.generatedAt ?? new Date();
  const plan = planPractitionerPdf(result, options);
  const doc = new PdfDocument();
  const period = formatCompactPeriodLabel(result.period, locale);
  doc.footerLeft = `${copy(labels, 'brand', 'Clinio')} · ${copy(labels, 'footerReport', 'Practitioner report')} · ${period}`;
  doc.footerRightPrefix = copy(labels, 'pageLabel', 'Page');
  doc.footerOf = copy(labels, 'pageOf', 'of');
  drawHeader(doc, result, labels, generatedAt, locale);
  renderPlan(doc, plan, result, labels, locale);
  return doc.build();
}

export { planPractitionerPdf };
export type { PractitionerPdfPlan };
